import { useState, RefObject } from 'react';

// Função utilitária para arredondar valores de porcentagem
export const roundPercentage = (value: number): number => {
  return Math.round(value * 100) / 100;
};

// Função para limpar larguras não arredondadas em tabelas existentes
export const cleanExistingTableWidths = (table: HTMLTableElement) => {
  const colgroup = table.querySelector('colgroup');
  if (colgroup) {
    const cols = colgroup.querySelectorAll('col');
    cols.forEach(col => {
      const width = col.style.width;
      if (width && width.includes('%')) {
        const numericValue = parseFloat(width);
        if (!isNaN(numericValue)) {
          col.style.width = `${roundPercentage(numericValue)}%`;
        }
      }
    });
  }
};

export const updateTableColumnWidths = (table: HTMLTableElement, widths: number[]) => {
  // Garantir que a tabela tenha table-layout: fixed
  table.style.tableLayout = 'fixed';

  // Criar ou atualizar colgroup
  let colgroup = table.querySelector('colgroup');
  if (!colgroup) {
    colgroup = document.createElement('colgroup');
    table.insertBefore(colgroup, table.firstChild);
  }

  // Limpar colunas existentes
  colgroup.innerHTML = '';

  // Adicionar novas colunas com larguras
  widths.forEach(width => {
    const col = document.createElement('col');
    col.style.width = `${roundPercentage(width)}%`;
    colgroup!.appendChild(col);
  });
};

interface UseTableResizeProps {
  editorRef: RefObject<HTMLDivElement>;
  onContentChange: (content: string, forceHistory: boolean) => void;
}

export const useTableResize = ({ editorRef, onContentChange }: UseTableResizeProps) => {
  // Estados para redimensionamento de colunas
  const [isResizingColumn, setIsResizingColumn] = useState(false);
  const [resizingColumnData, setResizingColumnData] = useState<{
    table: HTMLTableElement;
    columnIndex: number;
    startX: number;
    startWidths: number[];
  } | null>(null);

  // Estados para redimensionamento de linhas
  const [isResizingRow, setIsResizingRow] = useState(false);
  const [resizingRowData, setResizingRowData] = useState<{
    table: HTMLTableElement;
    rowIndex: number;
    startY: number;
    startHeights: number[];
  } | null>(null);

  // Estados para redimensionamento da tabela
  const [isResizingTable, setIsResizingTable] = useState(false);
  const [resizingTableData, setResizingTableData] = useState<{
    table: HTMLTableElement;
    startX: number;
    startWidth: number;
    containerWidth: number;
  } | null>(null);

  const handleColumnResizeStart = (e: MouseEvent, table: HTMLTableElement, columnIndex: number) => {
    e.preventDefault();

    // Determinar o total lógico de colunas baseando-se nas larguras totais e colspans
    let totalLogicalCols = 0;
    const allRows = table.querySelectorAll('tr');
    if (allRows.length > 0) {
      const firstRowCells = allRows[0].querySelectorAll('td, th');
      firstRowCells.forEach(cell => {
        totalLogicalCols += parseInt(cell.getAttribute('colspan') || '1', 10);
      });
    }

    // Obter larguras atuais das colunas
    const cols = table.querySelectorAll('col');
    const currentWidths: number[] = [];

    // Se as colunas pré-definidas (colgroup) baterem com a realidade lógica
    if (cols.length === totalLogicalCols && cols.length > 0) {
      cols.forEach(col => {
        const width = col.style.width;
        if (width && width.includes('%')) {
          currentWidths.push(parseFloat(width));
        } else {
          currentWidths.push(roundPercentage(100 / cols.length));
        }
      });
    } else {
      // Procurar pela primeira linha sem células mescladas (onde o total de TDs é igual ao lógico)
      const targetRow = Array.from(allRows).find(row => {
        return row.querySelectorAll('td, th').length === totalLogicalCols;
      });

      if (targetRow) {
        const cells = targetRow.querySelectorAll('td, th');
        const tableWidth = Math.max(1, table.offsetWidth);
        for (let i = 0; i < cells.length; i++) {
          const cellElement = cells[i] as HTMLElement;
          const cellWidth = cellElement.offsetWidth;
          currentWidths.push(roundPercentage((cellWidth / tableWidth) * 100));
        }
      } else {
        // Fallback absoluto: divide o tamanho igual para tudo (para tabelas muito disformes)
        const fallbackPercent = roundPercentage(100 / Math.max(1, totalLogicalCols));
        for (let i = 0; i < totalLogicalCols; i++) {
          currentWidths.push(fallbackPercent);
        }
      }
    }

    setIsResizingColumn(true);
    setResizingColumnData({
      table,
      columnIndex,
      startX: e.clientX,
      startWidths: currentWidths
    });
  };

  const handleColumnResizeMove = (e: MouseEvent) => {
    if (!isResizingColumn || !resizingColumnData) return;

    const { table, columnIndex, startX, startWidths } = resizingColumnData;
    const deltaX = e.clientX - startX;
    const tableWidth = table.offsetWidth;
    const deltaPercent = (deltaX / tableWidth) * 100;

    const newWidths = [...startWidths];
    const currentWidth = newWidths[columnIndex];
    const nextColumnIndex = columnIndex + 1;

    if (nextColumnIndex < newWidths.length) {
      const nextWidth = newWidths[nextColumnIndex];
      const minWidth = 5; // Largura mínima de 5%

      // Limitar o deltaPercent para que as colunas não fiquem menores que minWidth
      let effectiveDelta = deltaPercent;
      if (deltaPercent < 0 && currentWidth + deltaPercent < minWidth) {
        effectiveDelta = minWidth - currentWidth;
      } else if (deltaPercent > 0 && nextWidth - deltaPercent < minWidth) {
        effectiveDelta = nextWidth - minWidth;
      }

      // Calcular novas larguras
      const newCurrentWidth = currentWidth + effectiveDelta;
      const newNextWidth = nextWidth - effectiveDelta;

      // Verificar se as larguras são válidas (redundante, mas seguro)
      if (newCurrentWidth >= minWidth && newNextWidth >= minWidth) {
        newWidths[columnIndex] = roundPercentage(newCurrentWidth);
        newWidths[nextColumnIndex] = roundPercentage(newNextWidth);

        // Aplicar as novas larguras
        updateTableColumnWidths(table, newWidths);
      }
    }
  };

  const handleColumnResizeEnd = () => {
    // Remover classe 'resizing' de todos os handles
    if (editorRef.current) {
      editorRef.current.querySelectorAll('.column-resize-handle.resizing').forEach(handle => {
        handle.classList.remove('resizing');
      });
    }

    setIsResizingColumn(false);
    setResizingColumnData(null);

    // Salvar mudanças no histórico
    if (editorRef.current) {
      onContentChange(editorRef.current.innerHTML, true);
    }
  };

  // Funções para redimensionamento de linhas
  const handleRowResizeStart = (e: MouseEvent, table: HTMLTableElement, rowIndex: number) => {
    e.preventDefault();

    // Obter alturas atuais das linhas
    const rows = table.querySelectorAll('tr');
    const currentHeights: number[] = [];

    rows.forEach(row => {
      const rowElement = row as HTMLTableRowElement;
      const height = rowElement.style.height;
      if (height && height.includes('px')) {
        currentHeights.push(parseFloat(height));
      } else {
        // Se não tem altura definida, usar altura computada
        currentHeights.push(rowElement.offsetHeight);
      }
    });

    setIsResizingRow(true);
    setResizingRowData({
      table,
      rowIndex,
      startY: e.clientY,
      startHeights: currentHeights
    });
  };

  const handleRowResizeMove = (e: MouseEvent) => {
    if (!isResizingRow || !resizingRowData) return;

    const { table, rowIndex, startY, startHeights } = resizingRowData;
    const deltaY = e.clientY - startY;
    const minHeight = 25; // Altura mínima de 25px

    const newHeights = [...startHeights];
    const currentHeight = newHeights[rowIndex];

    // Limitar o deltaY para que a linha não fique menor que minHeight
    let effectiveDelta = deltaY;
    if (deltaY < 0 && currentHeight + deltaY < minHeight) {
      effectiveDelta = minHeight - currentHeight;
    }

    // Calcular nova altura (afeta apenas a linha de cima do handle)
    const newCurrentHeight = currentHeight + effectiveDelta;

    // Verificar se a altura é válida
    if (newCurrentHeight >= minHeight) {
      newHeights[rowIndex] = newCurrentHeight;

      // Remover a altura fixa da tabela para permitir que ela expanda livremente
      table.style.height = 'auto';

      // Aplicar a nova altura APENAS na linha atual, sem travar o resto
      const rows = table.querySelectorAll('tr');
      if (rows.length > rowIndex) {
        (rows[rowIndex] as HTMLTableRowElement).style.height = `${newCurrentHeight}px`;
      }
    }
  };

  const handleRowResizeEnd = () => {
    // Remover classe 'resizing' de todos os handles de linha
    if (editorRef.current) {
      editorRef.current.querySelectorAll('.row-resize-handle.resizing').forEach(handle => {
        handle.classList.remove('resizing');
      });
    }

    setIsResizingRow(false);
    setResizingRowData(null);

    // Salvar mudanças no histórico
    if (editorRef.current) {
      onContentChange(editorRef.current.innerHTML, true);
    }
  };

  // Calcula a largura real de conteúdo do container (descontando padding)
  const getContentAreaWidth = (element: HTMLElement): number => {
    const computed = window.getComputedStyle(element);
    const paddingLeft = parseFloat(computed.paddingLeft) || 0;
    const paddingRight = parseFloat(computed.paddingRight) || 0;
    return element.clientWidth - paddingLeft - paddingRight;
  };

  // Funções para redimensionamento da tabela
  const handleTableResizeStart = (e: MouseEvent, table: HTMLTableElement) => {
    e.preventDefault();

    const container = editorRef.current;
    const containerWidth = container ? getContentAreaWidth(container) : 800;

    setIsResizingTable(true);
    setResizingTableData({
      table,
      startX: e.clientX,
      startWidth: table.offsetWidth,
      containerWidth,
    });
  };

  const handleTableResizeMove = (e: MouseEvent) => {
    if (!isResizingTable || !resizingTableData) return;

    const { table, startX, startWidth, containerWidth } = resizingTableData;
    if (containerWidth <= 0) return;

    const deltaX = e.clientX - startX;

    const minPercent = 20;
    const minPx = containerWidth * minPercent / 100;
    const newWidthPx = Math.min(Math.max(minPx, startWidth + deltaX), containerWidth);
    const newPercent = roundPercentage((newWidthPx / containerWidth) * 100);

    if (isNaN(newPercent) || newPercent >= 95) {
      table.style.width = '100%';
      table.style.tableLayout = 'fixed';
      table.removeAttribute('data-custom-width');
    } else {
      table.style.width = `${newPercent}%`;
      table.style.tableLayout = 'fixed';
      table.setAttribute('data-custom-width', newPercent.toString());
    }

    table.style.height = 'auto';
    table.removeAttribute('data-custom-height');
  };

  const handleTableResizeEnd = () => {
    if (editorRef.current) {
      editorRef.current.querySelectorAll('.table-resize-handle.resizing').forEach(handle => {
        handle.classList.remove('resizing');
      });
    }

    if (resizingTableData) {
      const { table } = resizingTableData;
      const customWidth = table.getAttribute('data-custom-width');

      if (customWidth) {
        table.style.width = `${customWidth}%`;
        table.style.tableLayout = 'fixed';
      } else {
        table.style.width = '100%';
        table.style.tableLayout = 'fixed';
      }
      table.style.height = 'auto';
    }

    setIsResizingTable(false);
    setResizingTableData(null);

    if (editorRef.current) {
      onContentChange(editorRef.current.innerHTML, true);
    }
  };

  return {
    isResizingColumn,
    isResizingRow,
    isResizingTable,
    resizingColumnData,
    resizingRowData,
    resizingTableData,
    handleColumnResizeStart,
    handleColumnResizeMove,
    handleColumnResizeEnd,
    handleRowResizeStart,
    handleRowResizeMove,
    handleRowResizeEnd,
    handleTableResizeStart,
    handleTableResizeMove,
    handleTableResizeEnd,
  };
};
